import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Printer, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

export default function PrintTemplates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const pg = usePagination(templates);
  const load = () => api.get("/print-templates").then(r => setTemplates(r.data)).catch(e => toast.error(formatErr(e)));
  useEffect(() => { load(); }, []);

  const del = async (tp) => {
    if (!confirm(`Delete print template "${tp.name}"?`)) return;
    try {
      await api.delete(`/print-templates/${tp.id}`);
      toast.success("Template deleted");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="p-8 max-w-5xl" data-testid="print-templates-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Printer className="w-3 h-3"/> Settings · Print Settings</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter" data-testid="print-templates-title">Print Template Master</h1>
          <div className="text-xs text-muted-foreground mt-2">Custom print templates with per-template paper, header, footer and totals options</div>
        </div>
        <Button data-testid="new-template-btn" onClick={() => navigate("/settings/print-templates/new")}
          className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs">
          <Plus className="w-4 h-4 mr-1"/> New Template
        </Button>
      </div>

      <div className="border border-border rounded-sm overflow-hidden" data-testid="print-template-master">
        <table className="w-full text-sm" data-testid="print-template-table">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Template Name</th>
              <th className="text-left p-3">Paper Size</th>
              <th className="text-left p-3">Orientation</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pg.pageItems.map(tp => (
              <tr key={tp.id} data-testid={`pt-row-${tp.id}`} className="border-t border-border hover:bg-secondary/50">
                <td className="p-3 font-semibold">{tp.name}</td>
                <td className="p-3 text-xs font-mono">{tp.paper_size}</td>
                <td className="p-3 text-xs capitalize">{tp.orientation}</td>
                <td className="p-3" data-testid={`pt-status-${tp.id}`}>
                  {tp.status === "Active"
                    ? <Badge className="rounded-sm text-[10px] uppercase tracking-wider">Active</Badge>
                    : <Badge variant="secondary" className="rounded-sm text-[10px] uppercase tracking-wider text-muted-foreground">Inactive</Badge>}
                </td>
                <td className="p-3 text-right">
                  <Button data-testid={`pt-edit-${tp.id}`} size="sm" variant="ghost" onClick={() => navigate(`/settings/print-templates/${tp.id}/edit`)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>
                  <Button data-testid={`pt-del-${tp.id}`} size="sm" variant="ghost" onClick={() => del(tp)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>
                </td>
              </tr>
            ))}
            {!templates.length && (
              <tr><td colSpan={5} className="p-8 text-center text-muted-foreground text-xs uppercase tracking-widest" data-testid="pt-empty">
                No print templates yet — create one with New Template
              </td></tr>
            )}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );
}
